import { inject } from '@angular/core';
import { AuthService } from '../services/auth.service';
import { Router } from '@angular/router';
import { SignInErrorResponse, SignInResponse } from '../interface/auth.interface';
import { StorageService } from '../../../shared/services/storage.service';
import { patchState, signalStore, withHooks, withMethods, withState } from '@ngrx/signals';
import { SessionStore } from '../../../shared/store/session.store';
import { setError, setFulfilled, setPending, withRequestStatus } from '../../../shared/store/features/request-status.feature';

const STORAGE_KEY = 'AUTH_STATE';

type AuthState = {
    accessToken: string;
    refreshToken: string;
    expiresIn: number;
    token: string;
    currentUserEmail: string;
};

const initialState: AuthState = {
    accessToken: null,
    refreshToken: null,
    expiresIn: null,
    token: null,
    currentUserEmail: null,
};

export const AuthStore = signalStore(
    { providedIn: 'root' },
    withState(initialState),
    withRequestStatus(),
    withMethods(
        (
            store,
            authService = inject(AuthService),
            router = inject(Router),
            storageService = inject(StorageService),
            sessionStore = inject(SessionStore),
        ) => ({
            signIn(email: string, password: string): void {
                patchState(store, setPending());
                authService.signIn(email, password).subscribe({
                    next: (response: SignInResponse) => {
                        if (response.IdToken) {
                            patchState(store, {
                                token: response.IdToken,
                                accessToken: response.AccessToken,
                                refreshToken: response.RefreshToken,
                                expiresIn: response.ExpiresIn,
                                currentUserEmail: email,
                            });
                            this._saveState();
                            patchState(store, setFulfilled());
                            router.navigate(['/admin']);
                        }
                    },
                    error: (error) => {
                        const response: SignInErrorResponse = error.error;
                        // In case of first sign in, user must change password
                        if (response.sessionId && response.type === 'NEW_PASSWORD_REQUIRED') {
                            sessionStore.sessionId.set(response.sessionId);
                            patchState(store, { currentUserEmail: email });
                            router.navigate(['/change-password']);
                        } else {
                            patchState(store, setError('error'));
                            throw error;
                        }
                    },
                });
            },

            changePassword(newPassword: string): void {
                if (store.currentUserEmail()?.length === 0 || sessionStore.sessionId()?.length === 0) {
                    return;
                }
                authService.changePassword(store.currentUserEmail(), newPassword, sessionStore.sessionId()).subscribe({
                    next: () => {
                        router.navigate(['/login']);
                    },
                });
            },

            refreshToken(): void {
                authService.refreshToken(store.refreshToken()).subscribe({
                    next: (response: SignInResponse) => {
                        patchState(store, {
                            token: response.IdToken,
                            accessToken: response.AccessToken,
                            refreshToken: store.refreshToken(),
                            expiresIn: response.ExpiresIn,
                        });
                        this._saveState();
                    },
                    error: () => {
                        this.reset();
                        router.navigate(['/login']);
                    },
                });
            },

            reset(): void {
                patchState(store, initialState);
                storageService.remove(STORAGE_KEY);
            },

            _saveState(): void {
                storageService.set<AuthState>(STORAGE_KEY, {
                    accessToken: store.accessToken(),
                    refreshToken: store.refreshToken(),
                    expiresIn: store.expiresIn(),
                    token: store.token(),
                    currentUserEmail: store.currentUserEmail(),
                });
            },
        }),
    ),
    withHooks({
        onInit(store, storageService = inject(StorageService)) {
            if (storageService.get<AuthState>(STORAGE_KEY)) {
                patchState(store, storageService.get(STORAGE_KEY));
            }
            // Refresh token every 60 seconds pour les personnes connectées
            if (store.accessToken()) {
                setInterval(() => {
                    store.refreshToken();
                }, 10 * 1000);
            }
        },
    }),
);
